// Translation Service for BeeVids
// Integrates with DeepL API for high-quality translations

class TranslationService {
  constructor(apiKeys) {
    this.deepLApiKey = apiKeys.deepL;
    this.baseURL = 'https://api-free.deepl.com/v2'; // Use free tier URL
  }

  // Get supported languages from DeepL
  async getSupportedLanguages() {
    try {
      const response = await fetch(`${this.baseURL}/languages?type=target`, {
        method: 'GET',
        headers: {
          'Authorization': `DeepL-Auth-Key ${this.deepLApiKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`DeepL API error: ${response.status}`);
      }

      const languages = await response.json();
      return languages.map(lang => ({
        code: lang.language.toLowerCase(),
        name: lang.name,
        supports_formality: lang.supports_formality || false
      }));
    } catch (error) {
      console.error('Error fetching supported languages:', error);
      // Return fallback languages
      return [
        { code: 'es', name: 'Spanish', supports_formality: false },
        { code: 'fr', name: 'French', supports_formality: true },
        { code: 'de', name: 'German', supports_formality: true },
        { code: 'it', name: 'Italian', supports_formality: true },
        { code: 'pt', name: 'Portuguese', supports_formality: true },
        { code: 'ru', name: 'Russian', supports_formality: true },
        { code: 'ja', name: 'Japanese', supports_formality: false },
        { code: 'ko', name: 'Korean', supports_formality: false },
        { code: 'zh', name: 'Chinese', supports_formality: false },
        { code: 'ar', name: 'Arabic', supports_formality: false }
      ];
    }
  }

    // Translate a single text segment  async translateText(text, targetLanguage, sourceLanguage = 'auto') {    if (!this.deepLApiKey) {      throw new Error('DeepL API key is required for translation');    }    try {      // Use Electron IPC to bypass CORS when available      if (window.require) {        const { ipcRenderer } = window.require('electron');                const requestData = {          text: text,          target_lang: targetLanguage.toUpperCase(),          source_lang: sourceLanguage !== 'auto' ? sourceLanguage.toUpperCase() : undefined,          auth_key: this.deepLApiKey        };        const result = await ipcRenderer.invoke('deepl-translate', requestData);                if (result.success && result.translations && result.translations.length > 0) {          return {            success: true,            translatedText: result.translations[0].text,            detectedLanguage: result.translations[0].detected_source_language,            confidence: 0.95          };        } else {          throw new Error(result.error || 'No translation returned from DeepL');        }      } else {        // Fallback for development - use direct fetch (will fail with CORS)        const formData = new FormData();        formData.append('text', text);        formData.append('target_lang', targetLanguage.toUpperCase());                if (sourceLanguage !== 'auto') {          formData.append('source_lang', sourceLanguage.toUpperCase());        }        const response = await fetch(`${this.baseURL}/translate`, {          method: 'POST',          headers: {            'Authorization': `DeepL-Auth-Key ${this.deepLApiKey}`          },          body: formData        });        if (!response.ok) {          const errorData = await response.text();          throw new Error(`DeepL API error: ${response.status} - ${errorData}`);        }        const result = await response.json();                if (result.translations && result.translations.length > 0) {          return {            success: true,            translatedText: result.translations[0].text,            detectedLanguage: result.translations[0].detected_source_language,            confidence: 0.95          };        } else {          throw new Error('No translation returned from DeepL');        }      }    } catch (error) {      return {        success: false,        error: error.message,        translatedText: text // Return original text on failure      };    }  }

  // Translate multiple segments with progress tracking
  async translateSegments(segments, targetLanguage, sourceLanguage = 'auto', onProgress) {
    const results = [];
    const batchSize = 5; // Translate 5 segments at a time to avoid rate limits
    
    for (let i = 0; i < segments.length; i += batchSize) {
      const batch = segments.slice(i, i + batchSize);
      const batchPromises = batch.map(async (segment) => {
        const result = await this.translateText(segment.text, targetLanguage, sourceLanguage);
        
        return {
          ...segment,
          text: result.translatedText,
          originalText: segment.text,
          translationLanguage: targetLanguage,
          detectedLanguage: result.detectedLanguage,
          translationConfidence: result.confidence || 0.95,
          translationError: result.success ? null : result.error
        };
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      // Report progress
      const progress = Math.round((results.length / segments.length) * 100);
      if (onProgress) {
        onProgress(progress, `Translated ${results.length}/${segments.length} segments`);
      }

      // Small delay to respect rate limits
      if (i + batchSize < segments.length) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    return {
      success: true,
      translatedSegments: results,
      totalSegments: segments.length,
      targetLanguage,
      sourceLanguage
    };
  }

  // Check API key validity
  async testApiKey() {
    try {
      const result = await this.translateText('Hello world', 'ES');
      return {
        success: result.success,
        message: result.success ? 'DeepL API key is valid!' : result.error
      };
    } catch (error) {
      return {
        success: false,
        message: `DeepL API test failed: ${error.message}`
      };
    }
  }

  // Get usage information
  async getUsageInfo() {
    try {
      const response = await fetch(`${this.baseURL}/usage`, {
        method: 'GET',
        headers: {
          'Authorization': `DeepL-Auth-Key ${this.deepLApiKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`DeepL API error: ${response.status}`);
      }

      const usage = await response.json();
      return {
        success: true,
        characterCount: usage.character_count,
        characterLimit: usage.character_limit,
        usage: usage.character_count / usage.character_limit * 100
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default TranslationService; 